/**
 * Toroidal arena topology (DECISIONS.md AMD-24 — "no edge").
 *
 * The arena is a flat torus: the 200×200 play field has no walls. Positions live in the
 * fundamental domain `[-100, 100)` on both axes and are canonicalised after every integration
 * step. Every distance and direction in the sim goes through this module — a single raw
 * `b.x - a.x` is a bug that only manifests within one screen of the seam.
 *
 * Determinism: pure arithmetic, no clock, no RNG, no allocation. `wrapDeltaX` takes the same
 * branch for the same input every time, so a replay is bit-identical.
 */

import { cfg } from '../core/config';
import { sweptCircle } from '../core/spatial-hash';

/**
 * Wrap extent. Read once at module load: `arena.width`/`arena.height` are `hot_reload: false`
 * (A.5), so the torus period is fixed for the lifetime of the process. Note this is deliberately
 * NOT `world.arenaHalfW` — the Hadal Sovereign contraction (§10.6) shrinks that as a soft corral
 * inside the fundamental domain, and the topology must not change mid-fight.
 */
export const WRAP_W: number = cfg.get('arena.width');
export const WRAP_H: number = cfg.get('arena.height');
export const WRAP_HALF_W: number = WRAP_W / 2;
export const WRAP_HALF_H: number = WRAP_H / 2;

/** Slow path: canonicalise an arbitrary magnitude into `[-half, half)`. */
function canon(v: number, period: number, half: number): number {
  const t = v + half;
  return t - Math.floor(t / period) * period - half;
}

/** Shortest signed x displacement across the seam. Input may be any raw `bx - ax`. */
export function wrapDeltaX(dx: number): number {
  if (dx >= -WRAP_HALF_W && dx < WRAP_HALF_W) return dx;
  const a = dx - WRAP_W;
  if (a >= -WRAP_HALF_W && a < WRAP_HALF_W) return a;
  const b = dx + WRAP_W;
  if (b >= -WRAP_HALF_W && b < WRAP_HALF_W) return b;
  return canon(dx, WRAP_W, WRAP_HALF_W);
}

/** Shortest signed y displacement across the seam. */
export function wrapDeltaY(dy: number): number {
  if (dy >= -WRAP_HALF_H && dy < WRAP_HALF_H) return dy;
  const a = dy - WRAP_H;
  if (a >= -WRAP_HALF_H && a < WRAP_HALF_H) return a;
  const b = dy + WRAP_H;
  if (b >= -WRAP_HALF_H && b < WRAP_HALF_H) return b;
  return canon(dy, WRAP_H, WRAP_HALF_H);
}

/** Canonicalise an absolute x into `[-100, 100)`. Identical map to `wrapDeltaX`. */
export function wrapX(x: number): number {
  return wrapDeltaX(x);
}

/** Canonicalise an absolute y into `[-100, 100)`. */
export function wrapY(y: number): number {
  return wrapDeltaY(y);
}

/** Canonicalise a position in place. Call after every integration step. */
export function wrapPos(pos: { x: number; y: number }): void {
  pos.x = wrapDeltaX(pos.x);
  pos.y = wrapDeltaY(pos.y);
}

/** Shortest squared distance between two points on the torus. */
export function wrapDist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = wrapDeltaX(bx - ax);
  const dy = wrapDeltaY(by - ay);
  return dx * dx + dy * dy;
}

/** Shortest distance between two points on the torus. */
export function wrapDist(ax: number, ay: number, bx: number, by: number): number {
  return Math.sqrt(wrapDist2(ax, ay, bx, by));
}

/**
 * X of the image of `bx` nearest to `ax`. Use this to build a locally-Euclidean frame around
 * `a` when a chunk of maths wants plain coordinates (swept tests, DDA segments, lead prediction).
 * The result may fall outside `[-100, 100)` — that is the point.
 */
export function nearestX(ax: number, bx: number): number {
  return ax + wrapDeltaX(bx - ax);
}

/** Y of the image of `by` nearest to `ay`. */
export function nearestY(ay: number, by: number): number {
  return ay + wrapDeltaY(by - ay);
}

/**
 * Swept circle-vs-circle on the torus. `dx,dy` must already be an unwrapped displacement
 * (the true motion of this tick, not the difference of two canonicalised positions).
 * Returns time of impact in `[0,1]` or -1.
 */
export function sweptCircleWrapped(
  ax: number,
  ay: number,
  dx: number,
  dy: number,
  ar: number,
  bx: number,
  by: number,
  br: number
): number {
  return sweptCircle(0, 0, dx, dy, ar, wrapDeltaX(bx - ax), wrapDeltaY(by - ay), br);
}

/**
 * Shortest distance from point `px,py` to the segment `ax,ay → ax+dx,ay+dy` on the torus,
 * squared. `dx,dy` is an unwrapped displacement. Used by beams and swept AoE.
 */
export function wrapSegDist2(ax: number, ay: number, dx: number, dy: number, px: number, py: number): number {
  const rx = wrapDeltaX(px - ax);
  const ry = wrapDeltaY(py - ay);
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return rx * rx + ry * ry;
  let t = (rx * dx + ry * dy) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = rx - dx * t;
  const cy = ry - dy * t;
  return cx * cx + cy * cy;
}
